import { useEffect, useRef, useState, type AriaRole, type ReactNode } from 'react';
import { Button } from '@/components/ui/primitives/Button';
import { X } from '@/components/ui/icons';

/** `alert` = something failed or was refused (the kit's one red, always with words); `info` = everything else. */
export type NoticeTone = 'info' | 'alert';

export interface NoticeAction {
  label: string;
  onAction: () => void;
  ariaLabel?: string;
  testId?: string;
}

export interface NoticeProps {
  /** `inline` = in its host's flow; `toast` = in the corner stack (FeedbackContext), usually auto-dismissed. */
  placement?: 'inline' | 'toast';
  tone?: NoticeTone;
  /**
   * `box` (default) = the kit banner; `quiet` = its words only (a one-line reason beside a control, clamped to 3
   * lines); `band` = a full-width line under a header (hairline below, no box).
   */
  look?: 'box' | 'quiet' | 'band';
  /**
   * Where it floats (none = in the flow): `top` = fixed, top centre of the screen (an app-level error over any
   * route); `bottom` = bottom centre of its positioned host (a hint over the sheet); `point` = above `at` in its
   * positioned host, taking no pointer events (a 2 s note at the ink).
   */
  anchor?: 'top' | 'bottom' | 'point';
  /** `anchor="point"`: the point, in the host's pixels. */
  at?: { left: number; top: number };
  as?: 'div' | 'p' | 'span';
  role?: AriaRole;
  children: ReactNode;
  onClose?: () => void;
  onRetry?: () => void;
  /** Auto-dismiss after this many ms (0 = stays); needs onClose. */
  duration?: number;
  /** One labelled action beside the text (a receipt's `Show #4`); the notice closes after it runs. */
  action?: NoticeAction;
  /**
   * The auto-dismiss clock stops while focus is inside (WCAG 2.2.1: a toast with a control must not vanish
   * under the person reaching for it) and resumes with the time it had left.
   */
  pauseOnFocus?: boolean;
  /** Extra controls after the text (a Retake / Dismiss pair, a Copy link). */
  extra?: ReactNode;
  testId?: string;
  className?: string;
  title?: string;
  id?: string;
  hidden?: boolean;
  'aria-label'?: string;
  [data: `data-${string}`]: string | number | undefined;
}

/**
 * THE NOTICE (spec--pmt-ui-redesign 5(b)): one notice for every toast, banner and inline message (10
 * implementations on main incl. `.alert`, AUDIT T5). The kit's `bk-banner` (square, hairline, ink rule on the left;
 * `bk-banner--alert` for a failure), in two placements.
 */
export function Notice({
  placement = 'inline',
  tone = 'info',
  look = 'box',
  anchor,
  at,
  as: As = 'div',
  role,
  children,
  onClose,
  onRetry,
  duration = 0,
  action,
  pauseOnFocus = false,
  extra,
  testId,
  className = '',
  title,
  id,
  hidden,
  'aria-label': ariaLabel,
  ...rest
}: NoticeProps) {
  // Auto-dismiss when duration > 0. The clock keeps the time it has LEFT across re-renders (a parent that
  // re-renders must not restart it) and across a focus pause.
  const [focused, setFocused] = useState(false);
  const paused = pauseOnFocus && focused;
  const left = useRef(duration);
  const close = useRef(onClose);
  close.current = onClose;
  const hasClose = !!onClose;
  useEffect(() => {
    if (!(duration > 0) || !hasClose || paused) return;
    const started = Date.now();
    const timer = setTimeout(() => close.current?.(), Math.max(0, left.current));
    return () => {
      clearTimeout(timer);
      left.current -= Date.now() - started;
    };
  }, [duration, hasClose, paused]);

  const cls = [
    'bk-banner',
    tone === 'alert' ? 'bk-banner--alert' : '',
    'pmt-notice',
    `pmt-notice--${placement}`,
    look !== 'box' ? `pmt-notice--${look}` : '',
    anchor ? `pmt-notice--at-${anchor}` : '',
    className,
  ].filter(Boolean).join(' ');
  const hasControls = !!(action || onRetry || onClose || extra);
  const Inner = As === 'div' ? 'div' : 'span';
  // a toast's controls are touch targets on every pointer (44 px; it floats over the sheet, Pencil included)
  const size = placement === 'toast' ? 'touch' : undefined;

  return (
    <As
      className={cls}
      role={role}
      title={title}
      id={id}
      hidden={hidden}
      aria-label={ariaLabel}
      data-testid={testId}
      data-tone={tone}
      style={anchor === 'point' && at ? { left: at.left, top: at.top } : undefined}
      onFocus={pauseOnFocus ? () => setFocused(true) : undefined}
      onBlur={pauseOnFocus ? (e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      } : undefined}
      data-paused={paused ? 'true' : undefined}
      {...rest}
    >
      <Inner className="pmt-notice__text">{children}</Inner>
      {hasControls && (
        <Inner className="pmt-notice__actions">
          {extra}
          {action && (
            <Button
              variant="ghost"
              size={size}
              onClick={() => {
                action.onAction();
                onClose?.();
              }}
              aria-label={action.ariaLabel ?? action.label}
              data-testid={action.testId}
            >
              {action.label}
            </Button>
          )}
          {onRetry && (
            <Button variant="ghost" size={size} onClick={onRetry} aria-label="Retry">
              Retry
            </Button>
          )}
          {onClose && <Button variant="ghost" size={size} icon={X} onClick={onClose} aria-label="Close" />}
        </Inner>
      )}
    </As>
  );
}
